import { usePlayer } from '@/composables/game/player'
import { useConfig } from '@/composables/game/config'
import { type SegmentationRule } from '@/types/shared/segmentation'
import { computed } from 'vue'
import { DateTime } from 'luxon'
import type { ECOMMERCE_ITEM_TAG } from '@/types/shared/player-config'

export const usePlayerSegmenter = () => {
  const { player, daysSinceJoined } = usePlayer()
  const { config } = useConfig()

  const lastPurchases = computed(() => player.value.ecommerce.lastPurchases)

  function segment(segmentation: SegmentationRule | undefined): boolean {
    if (!segmentation) return true

    const lastPurchasesLength = Object.keys(lastPurchases.value).length

    const {
      daysOnGame,
      levelReached,
      purchased,
      purchasedWithinDays,
      purchasedProductTypes,
    } = segmentation

    // DAYS ON GAME
    if (daysOnGame !== undefined && ((daysSinceJoined.value + 1) < daysOnGame)) {
      return false
    }

    // LEVEL REACHED
    if (levelReached !== undefined && player.value.level < levelReached) {
      return false
    }

    // PURCHASED EVER
    const hasPurchases = lastPurchasesLength > 0
    if (purchased !== undefined && purchased !== hasPurchases) {
      return false
    }

    // PURCHASED WITHIN DAYS
    if (purchasedWithinDays !== undefined) {
      const cutoff = DateTime.utc().minus({ days: purchasedWithinDays })
      const anyRecent = Object.values(lastPurchases.value).some(({ purchasedAt }) =>
        DateTime.fromSeconds(purchasedAt) >= cutoff,
      )
      if (!anyRecent) return false
    }

    // PURCHASED PRODUCT TYPES
    if (purchasedProductTypes !== undefined) {
      const allMatched = purchasedProductTypes.every(purchaseType =>
        Object.keys(lastPurchases.value).some(ecommerceItemId => {
          const item = config.value.game.ecommerce.items.find(i => i.id === ecommerceItemId)
          return item?.tags.includes(purchaseType as ECOMMERCE_ITEM_TAG) ?? false
        }),
      )
      if (!allMatched) return false
    }

    return true
  }

  return {
    segment,
  }
}